import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { Store } from '@ngrx/store';
import { AgreementActions } from '@store/actions/agreement/agreement.actions';
import { selectSelectedDocumentFile, selectDocumentUploadProgress, selectIsDocumentUploading, selectDocumentUploadComplete, selectDocumentUploadError } from '@store/selectors/agreement/agreement.selectors';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { Observable } from 'rxjs';
import { MAX_FILE_SIZE } from '@constants/file-upload';

@Component({
  selector: 'app-document-upload',
  imports: [
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatCardModule,
    CommonModule,
    MatSelectModule,
    MatButtonModule,
    NgxMatSelectSearchModule,
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule
  ],
  templateUrl: './document-upload.component.html',
  styleUrls: ['./document-upload.component.css'],
})
export class DocumentUploadComponent implements OnInit {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  
  selectedFile$!: Observable<File | null>;
  uploadProgress$!: Observable<number | null>;
  isUploading$!: Observable<boolean>;
  uploadComplete$!: Observable<boolean>;
  uploadError$!: Observable<string | null>;
  
  isDragging = false;
  fileSizeError: string | null = null;
  fileTypeError: string | null = null;
  
  private readonly MAX_FILE_SIZE = MAX_FILE_SIZE;
  private readonly ALLOWED_FILE_TYPES = ['application/pdf'];
  private readonly ALLOWED_EXTENSIONS = ['.pdf'];

  constructor(private store: Store) {}

  ngOnInit(): void {
    this.selectedFile$ = this.store.select(selectSelectedDocumentFile);
    this.uploadProgress$ = this.store.select(selectDocumentUploadProgress);
    this.isUploading$ = this.store.select(selectIsDocumentUploading);
    this.uploadComplete$ = this.store.select(selectDocumentUploadComplete);
    this.uploadError$ = this.store.select(selectDocumentUploadError);
  }

  onFileSelected(event: Event): void {
    const element = event.target as HTMLInputElement;
    if (element.files && element.files.length > 0) {
      const file = element.files[0];
      this.validateAndSelectFile(file);
    }
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
    
    if (event.dataTransfer && event.dataTransfer.files.length > 0) {
      const file = event.dataTransfer.files[0];
      this.validateAndSelectFile(file);
    }
  }

  private validateAndSelectFile(file: File): void {
    this.fileSizeError = null;
    this.fileTypeError = null;
    
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!this.ALLOWED_FILE_TYPES.includes(file.type) && !this.ALLOWED_EXTENSIONS.includes(fileExtension)) {
      this.fileTypeError = 'Only PDF files are allowed.';
      return;
    }
    
    if (file.size > this.MAX_FILE_SIZE) {
      this.fileSizeError = `File size exceeds the maximum limit of ${this.formatFileSize(this.MAX_FILE_SIZE)}. Selected file size: ${this.formatFileSize(file.size)}.`;
      return;
    }

    this.store.dispatch(AgreementActions.selectDocumentFile({ file }));
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  uploadFile(): void {
    this.selectedFile$.subscribe(file => {
      if (file) {
        this.store.dispatch(AgreementActions.uploadDocument({ file: file, documentTypeId: 1000 }));
      }
    }).unsubscribe();
  }

  clearFile(): void {
    this.store.dispatch(AgreementActions.clearSelectedDocumentFile());
    this.fileSizeError = null;
    this.fileTypeError = null;
  
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }
}